import { useMutation } from '@apollo/client';
import { AnalyticIdentifier } from '@common/neo.tax';
import { AlertModalLayout, Button } from '@ui-components/neo.tax';
import { useState } from 'react';

import {
  DeletePayrollMonthExpenseSyncEventMutationVariables,
  DeletePayrollSyncEventForFiscalYearMutation,
  DELETE_PAYROLL_MONTH_EXPENSE_SYNC_EVENT,
  evictCache,
  GetCompanyQueryVariables,
  GET_COMPANY,
  useAnalyticId,
  useCurrentCompanyId,
  DataSourcesTableItem,
} from '@neo.tax/shared';

import { GeneralFormErrorStyled, ModalContainer } from './delete-payroll-month-expense-sync-event.styled';

interface DeletePayrollMonthExpenseSyncEventProps {
  itemToDelete: DataSourcesTableItem;
  close: () => void;
}

export const DeletePayrollMonthExpenseSyncEvent = ({
  close,
  itemToDelete,
}: DeletePayrollMonthExpenseSyncEventProps): React.ReactElement | null => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const companyId = useCurrentCompanyId();
  const confirmBtnId = useAnalyticId(AnalyticIdentifier.DELETE_CONNECTED_DATA_SOURCE_CONFIRM_BUTTON);
  const cancelBtnId = useAnalyticId(AnalyticIdentifier.DELETE_CONNECTED_DATA_SOURCE_CANCEL_BUTTON);

  const [deletePayrollMonthExpenseSyncEvent, { loading }] = useMutation<
    DeletePayrollSyncEventForFiscalYearMutation,
    DeletePayrollMonthExpenseSyncEventMutationVariables
  >(DELETE_PAYROLL_MONTH_EXPENSE_SYNC_EVENT, {
    refetchQueries: [
      {
        query: GET_COMPANY,
        variables: {
          companyId,
        } satisfies GetCompanyQueryVariables,
      },
    ],
    awaitRefetchQueries: true,
    update: (cache) => {
      evictCache(cache, ['getContributor', 'getAscWages']);
    },
  });

  const onSubmit = async (): Promise<void> => {
    try {
      await deletePayrollMonthExpenseSyncEvent({
        variables: {
          companyId,
          payrollMonthExpenseSyncEventId: itemToDelete.id,
        },
      });
      close();
    } catch {
      setErrorMessage('Something unexpected prevented the file from being deleted. Please try again.');
    }
  };

  return (
    <ModalContainer>
      <AlertModalLayout
        title={`Delete ${itemToDelete.title}`}
        copy={`This will permanently remove all expenses from this data source and all allocations associated with those expenses. Are you sure you want to delete ${itemToDelete.title}`}
        trailingContent={
          <>
            <Button id={cancelBtnId} size="base" variant="white" rounded="regular" onClick={close}>
              Cancel
            </Button>
            <Button
              id={confirmBtnId}
              disabled={loading}
              rounded="regular"
              size="base"
              type="submit"
              variant="rsbry"
              onClick={onSubmit}
            >
              {loading ? 'Deleting...' : 'Confirm'}
            </Button>
          </>
        }
      >
        <GeneralFormErrorStyled centered error={!!errorMessage}>
          {errorMessage}
        </GeneralFormErrorStyled>
      </AlertModalLayout>
    </ModalContainer>
  );
};
